import { useEffect, useMemo, useState } from 'react';
import { Check, Layers, Plus, ShieldCheck, X } from 'lucide-react';
import type {
  CataloguePackage, CatalogueTest, ComposedSet, ComposedTest, CursorPage,
  InclusionLevel, MedicalConfigStatus, MedicalConfiguration, Recommendation, RecommendedPackage,
} from '@dru/types';
import { BRAND, INCLUSION_LABEL } from '@dru/types';
import {
  Alert, Button, Card, ConfirmDialog, InfoTip, StatusBadge, TextField, TextSkeleton, cn,
  useIdempotencyKey, type Tone,
} from '@dru/ui';
import { PageHeader } from '../layouts/ConsoleShell';
import { api } from '../lib/api';
import { fetchAll } from '../lib/fetchAll';
import { useQuery } from '../lib/useQuery';
import { errorMessage } from '../lib/auth';

/**
 * The medical-configuration builder and viewer, shared by two consoles.
 *
 * HR builds a configuration for its own company; the platform's support
 * team builds one on a company's behalf from the company page. Same
 * sequence — packages → tests → who it is for — and the
 * only thing that differs is which endpoint the save goes to, so that is a
 * prop. Two copies of a four-step wizard would be two places for the
 * "mandatory tests cannot be dropped" rule to diverge.
 *
 * The screen is careful about one thing above all: it never implies that
 * choosing packages decides what a worker is owed. That still comes from
 * their job role's hazards, and the note at the end says so.
 */

export const TONE: Record<InclusionLevel, 'covered' | 'pending' | 'neutral'> = {
  MANDATORY: 'covered',
  RECOMMENDED: 'pending',
  OPTIONAL: 'neutral',
};

/**
 * A configuration's state in words, the same on the list and the detail.
 * "draft" alone did not say the thing that matters — that it is not in force.
 */
export function configStatus(status: MedicalConfigStatus, version: number): { label: string; tone: Tone } {
  switch (status) {
    case 'PUBLISHED': return { label: `In force · v${version}`, tone: 'covered' };
    case 'DRAFT': return { label: 'Draft — not in force', tone: 'pending' };
    default: return { label: 'Superseded', tone: 'neutral' };
  }
}

export interface BuilderPaths {
  /** Where configurations are created and read: `/medical-configurations` or the admin setup path. */
  configs: string;
  /** No longer read: departments were removed (26 Oct 2026). Kept optional for callers. */
  departments?: string;
}

// No industry step (owner's review, 14 Oct 2026): packages are chosen
// directly. Every configuration is for all employees: departments were
// removed from the product on 26 Oct 2026.
type Step = 'packages' | 'tests' | 'name';

const STEPS: Array<{ key: Step; label: string }> = [
  { key: 'packages', label: 'Packages' },
  { key: 'tests', label: 'Tests' },
  { key: 'name', label: 'Save' },
];

export function Builder({ onCancel, onSaved, paths, industryId, canPublish = false }: {
  onCancel: () => void;
  /** Told what was saved: a draft, or a configuration now in force. */
  onSaved: (outcome: { published: boolean }) => void;
  paths: BuilderPaths;
  /**
   * The company's industry, when it has one. Its recommended packages are
   * marked, and ticked to start with (flow audit 2.3).
   */
  industryId?: string | null;
  /** Offers "Save and publish" beside "Save as draft" on the last step. */
  canPublish?: boolean;
}) {
  const packages = useQuery<CursorPage<CataloguePackage>>(() => api.get('/packages'), []);
  // What the industry suggests. Read-only and advisory: a failure leaves the
  // list as it was, unmarked, rather than blocking the builder.
  const recommendation = useQuery<Recommendation | null>(
    () => (industryId
      ? api.post<Recommendation>(`/industries/${industryId}/recommendation`, {}).catch(() => null)
      : Promise.resolve(null)),
    [industryId],
  );
  const idem = useIdempotencyKey();
  // `/tests` is paged now, so one call would return the first page and look
  // like the whole catalogue. Followed to the end.
  const allTests = useQuery<CatalogueTest[]>(
    () => fetchAll<CatalogueTest>((cursor) => api.get<CursorPage<CatalogueTest>>(
      `/tests?brief=true&limit=200${cursor ? `&cursor=${cursor}` : ''}`,
    )),
    [],
  );

  const [step, setStep] = useState<Step>('packages');
  const [chosen, setChosen] = useState<string[]>([]);
  /** Recommended packages by catalogue id. Matched by code as well, in case the catalogue moved on a version. */
  const suggested = useMemo(() => {
    const out = new Map<string, RecommendedPackage>();
    const recs = recommendation.data?.packages ?? [];
    for (const p of packages.data?.items ?? []) {
      const r = recs.find((x) => x.id === p.id) ?? recs.find((x) => x.code === p.code);
      if (r) out.set(p.id, r);
    }
    return out;
  }, [recommendation.data, packages.data]);
  // Ticked once, when both lists have arrived and nobody has ticked anything:
  // the industry's mandatory and recommended packages are the starting point.
  const [preselected, setPreselected] = useState(false);
  useEffect(() => {
    if (preselected || !suggested.size) return;
    setPreselected(true);
    setChosen((c) => (c.length ? c
      : [...suggested].filter(([, r]) => r.tier !== 'OPTIONAL').map(([id]) => id)));
  }, [suggested, preselected]);
  const [added, setAdded] = useState<string[]>([]);
  const [removed, setRemoved] = useState<string[]>([]);
  const [composed, setComposed] = useState<ComposedSet | null>(null);
  const [typedName, setTypedName] = useState<string | null>(null);
  // The name follows the packages chosen until somebody types their own
  // (flow audit 2.4): "All employees — Pre-employment basic".
  const autoName = () => {
    const who = 'All employees';
    const names = (packages.data?.items ?? []).filter((p) => chosen.includes(p.id)).map((p) => p.name);
    const what = names.length === 0 ? 'medical'
      : names.length <= 2 ? names.join(' + ')
      : `${names[0]} + ${names.length - 1} more`;
    return `${who} — ${what}`.slice(0, 120);
  };
  const name = typedName ?? autoName();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Recompose whenever the selection changes. The resolved set is the whole
  // point of the screen, so it must never lag behind the tick that changed it.
  useEffect(() => {
    if (step !== 'tests' && step !== 'name') return;
    let live = true;
    setError(null);
    api.post<ComposedSet>('/packages/compose', {
      packageIds: chosen, addTestIds: added, removeTestIds: removed,
    })
      .then((r) => { if (live) setComposed(r); })
      .catch((err) => { if (live) setError(errorMessage(err)); });
    return () => { live = false; };
  }, [step, chosen, added, removed]);

  const inSet = useMemo(
    () => new Set((composed?.tests ?? []).map((t) => t.id)),
    [composed],
  );

  // The draft once it exists. A publish that fails after the save must not
  // draft a second copy when the button is pressed again.
  const [draftId, setDraftId] = useState<string | null>(null);
  const publishIdem = useIdempotencyKey();

  async function save(publish: boolean) {
    setBusy(true);
    setError(null);
    let id = draftId;
    try {
      // A clinical record: the endpoint demands an idempotency key so a
      // retried save cannot create a second configuration.
      // One key for this save, held across retries: a second click after a
      // slow network replays the stored response instead of drafting twice.
      if (!id) {
        const created = await api.post<{ id: string }>(paths.configs, {
          name: name.trim(),
          packageIds: chosen,
          addTestIds: added,
          removeTestIds: removed,
        }, idem.key());
        idem.rotate();
        id = created.id;
        setDraftId(id);
      }
      if (publish) {
        await api.post(`${paths.configs}/${id}/publish`, {}, publishIdem.key());
        publishIdem.rotate();
      }
      onSaved({ published: publish });
    } catch (err) {
      setError(publish && id
        ? `Saved as a draft, but not published: ${errorMessage(err)}`
        : errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="New medical configuration"
        actions={<Button variant="ghost" onClick={onCancel}>Cancel</Button>}
      />

      <Stepper current={step} />

      {error && <Alert tone="danger">{error}</Alert>}

      {step === 'packages' && (
        <Card className="p-5">
          <h2 className="text-h4 text-neutral-900">Choose packages</h2>
          {packages.loading && <TextSkeleton lines={4} />}
          {packages.error && <Alert tone="danger">{packages.error}</Alert>}
          {suggested.size > 0 && recommendation.data?.industry && (
            <p className="mt-1 text-small text-neutral-600">
              Recommended for {recommendation.data.industry.name} are marked and ticked to start with.
            </p>
          )}
          <ul className="mt-4 grid gap-2 sm:grid-cols-2">
            {/* Recommended first: they are the answer to "what do I pick". */}
            {[...(packages.data?.items ?? [])].sort((a, b) => Number(suggested.has(b.id)) - Number(suggested.has(a.id))).map((p) => {
              const on = chosen.includes(p.id);
              const rec = suggested.get(p.id);
              return (
                <li key={p.id}>
                  <label
                    className={cn(
                      'flex cursor-pointer items-center gap-3 rounded-card border p-3',
                      on ? 'border-primary-500 bg-primary-50' : 'border-neutral-200 hover:bg-neutral-50',
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => setChosen((c) =>
                        on ? c.filter((x) => x !== p.id) : [...c, p.id])}
                      className="h-4 w-4 shrink-0 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block text-body font-medium text-neutral-900">{p.name}</span>
                      {rec && <span className="block text-caption text-neutral-500">{rec.because}</span>}
                    </span>
                    {rec && rec.tier !== 'OPTIONAL' && <StatusBadge tone="covered">Recommended</StatusBadge>}
                    <span className="shrink-0 text-caption tabular-nums text-neutral-500">{p.tests.length} tests</span>
                  </label>
                </li>
              );
            })}
          </ul>

          <div className="mt-4 flex justify-end">
            <Button onClick={() => setStep('tests')} disabled={chosen.length === 0}>
              Review {chosen.length} {chosen.length === 1 ? 'package' : 'packages'}
            </Button>
          </div>
        </Card>
      )}

      {step === 'tests' && (
        <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
          <Card className="p-5">
            <div className="flex items-start gap-2">
              <Layers className="mt-0.5 h-4 w-4 shrink-0 text-primary-600" />
              <div>
                <h2 className="flex items-center gap-1 text-h4 text-neutral-900">
                  {composed?.tests.length ?? 0} tests
                  <InfoTip label="About the tests">
                    Combined from your packages. A test in two packages appears once.
                  </InfoTip>
                </h2>
              </div>
            </div>

            <ul className="mt-4 divide-y divide-neutral-100">
              {(composed?.tests ?? []).map((t) => (
                <TestRow
                  key={t.id}
                  test={t}
                  onRemove={() => {
                    if (t.addedIndividually) setAdded((a) => a.filter((x) => x !== t.id));
                    else setRemoved((r) => [...r, t.id]);
                  }}
                />
              ))}
            </ul>

            {removed.length > 0 && (
              <button
                type="button"
                onClick={() => setRemoved([])}
                className="mt-3 text-caption font-medium text-primary-700 hover:underline"
              >
                Restore {removed.length} removed {removed.length === 1 ? 'test' : 'tests'}
              </button>
            )}
          </Card>

          <Card className="p-5">
            <h2 className="flex items-center gap-1 text-h4 text-neutral-900">
              Add a test
              <InfoTip label="About adding a test">Anything the packages do not already cover.</InfoTip>
            </h2>
            <ul className="mt-3 max-h-[26rem] space-y-1 overflow-y-auto">
              {(allTests.data ?? [])
                .filter((t) => !inSet.has(t.id))
                .map((t) => (
                  <li key={t.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setRemoved((r) => r.filter((x) => x !== t.id));
                        setAdded((a) => (a.includes(t.id) ? a : [...a, t.id]));
                      }}
                      className="flex w-full items-center gap-2 rounded-btn px-2 py-1.5 text-left hover:bg-neutral-50"
                    >
                      <Plus className="h-3.5 w-3.5 shrink-0 text-neutral-400" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-small text-neutral-800">{t.name}</span>
                        <span className="block font-mono text-caption text-neutral-500">{t.code}</span>
                      </span>
                    </button>
                  </li>
                ))}
            </ul>
          </Card>

          <div className="lg:col-span-2 flex items-center justify-between">
            <Button variant="ghost" onClick={() => setStep('packages')}>Back</Button>
            <Button onClick={() => setStep('name')} disabled={!composed?.tests.length}>
              Continue
            </Button>
          </div>
        </div>
      )}

      {step === 'name' && (
        <Card className="p-5">
          <h2 className="text-h4 text-neutral-900">Name it</h2>

          <div className="mt-4 max-w-md space-y-4">
            <TextField
              label="Name"
              required
              value={name}
              onChange={setTypedName}
            />
          </div>

          <div className="mt-5 rounded-card bg-neutral-50 p-3">
            <div className="text-caption font-semibold uppercase tracking-wider text-neutral-600">
              For all employees
            </div>
            <div className="mt-1 text-small text-neutral-700">
              {chosen.length} {chosen.length === 1 ? 'package' : 'packages'} ·{' '}
              {composed?.tests.length ?? 0} tests
              {added.length > 0 && <> · {added.length} added</>}
              {removed.length > 0 && <> · {removed.length} removed</>}
            </div>
            <div className="mt-1 text-caption text-neutral-500">
              {canPublish
                ? 'Publishing puts it in force for examinations raised from now on, and it can no longer be edited; whatever was in force for the same employees is kept for history. A draft applies only once it is published.'
                : 'A draft is not in force. It applies only once it is published.'}
            </div>
          </div>

          <ObligationNote />

          <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
            <Button variant="ghost" onClick={() => setStep('tests')} disabled={busy}>Back</Button>
            <div className="flex flex-wrap gap-2">
              <Button variant={canPublish ? 'secondary' : 'primary'} onClick={() => save(false)} loading={busy && !canPublish} disabled={busy || name.trim().length < 2 || Boolean(draftId)}>
                Save as draft
              </Button>
              {canPublish && (
                <Button onClick={() => save(true)} loading={busy} disabled={busy || name.trim().length < 2}>
                  Save and publish
                </Button>
              )}
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

function TestRow({ test, onRemove }: { test: ComposedTest; onRemove: () => void }) {
  return (
    <li className="flex items-center gap-3 py-2">
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2">
          <span className="text-small text-neutral-900">{test.name}</span>
          <StatusBadge tone={TONE[test.inclusion]}>{INCLUSION_LABEL[test.inclusion]}</StatusBadge>
        </span>
        <span className="mt-0.5 block text-caption text-neutral-500">
          <span className="font-mono">{test.code}</span>
          {test.fromPackages.length > 0 && <> · {test.fromPackages.join(', ')}</>}
          {test.addedIndividually && <> · added</>}
        </span>
      </span>
      {/* A mandatory test from a package cannot be dropped — the API refuses
          it, so offering the control would be offering a button that fails. */}
      {(test.inclusion !== 'MANDATORY' || test.addedIndividually) && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${test.name}`}
          className="rounded-btn p-1.5 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </li>
  );
}

function Stepper({ current }: { current: Step }) {
  const at = STEPS.findIndex((s) => s.key === current);
  return (
    <ol aria-label="Steps" className="flex flex-wrap items-center gap-x-2 gap-y-1">
      {STEPS.map((s, i) => (
        <li key={s.key} aria-current={i === at ? 'step' : undefined} className="flex items-center gap-2">
          <span
            className={cn(
              'flex h-6 w-6 items-center justify-center rounded-full text-caption font-semibold',
              i < at && 'bg-success-100 text-success-600',
              i === at && 'bg-primary-600 text-white',
              i > at && 'bg-neutral-100 text-neutral-500',
            )}
          >
            {i < at ? <Check aria-label="done" className="h-3.5 w-3.5" /> : i + 1}
          </span>
          <span className={cn('text-caption', i === at ? 'font-semibold text-neutral-900' : 'text-neutral-500')}>
            {s.label}
          </span>
          {i < STEPS.length - 1 && <span className="text-neutral-300" aria-hidden>→</span>}
        </li>
      ))}
    </ol>
  );
}

/**
 * The one thing this screen must not let somebody believe.
 */
export function ObligationNote() {
  return (
    <p className="mt-4 flex items-start gap-2 text-caption text-neutral-500">
      <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <span>
        This is what you have chosen to run. What an employee is legally owed comes
        from their job role’s hazards and is enforced separately — a statutory
        test stays required whether or not it appears here.
      </span>
    </p>
  );
}

/* ────────────────────────────────────────────────────────────────────
   Detail: read one, and publish it
   ──────────────────────────────────────────────────────────────────── */

export function Detail({ id, onBack, paths, canPublish = true, onPublished }: {
  id: string; onBack: () => void; paths: BuilderPaths; canPublish?: boolean;
  /** Told as soon as it is published, so a progress figure elsewhere follows without a reload. */
  onPublished?: () => void;
}) {
  const config = useQuery<MedicalConfiguration>(() => api.get(`${paths.configs}/${id}`), [id, paths.configs]);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Throws, so the confirmation stays open with the reason. */
  async function publish() {
    setError(null);
    await api.post(`${paths.configs}/${id}/publish`, {});
    setConfirming(false);
    config.reload();
    onPublished?.();
  }

  const c = config.data;

  return (
    <div className="space-y-4">
      <PageHeader
        title={c?.name ?? 'Configuration'}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={onBack}>Back</Button>
            {c?.status === 'DRAFT' && canPublish && (
              <Button onClick={() => setConfirming(true)}>Publish</Button>
            )}
          </div>
        }
      />

      {error && <Alert tone="danger">{error}</Alert>}
      {config.loading && !c && <TextSkeleton lines={5} />}
      {config.error && <Alert tone="danger">{config.error}</Alert>}

      {c && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge tone={configStatus(c.status, c.configVersion).tone}>
              {configStatus(c.status, c.configVersion).label}
            </StatusBadge>
            <span className="text-small text-neutral-600">
              For {c.departmentName ? `the ${c.departmentName} department (departments no longer apply)` : c.jobRole ? c.jobRole.title : 'all employees'}
            </span>
          </div>

          {c.status === 'DRAFT' && (
            <Alert tone="info" title="Not in force yet">
              {canPublish
                ? 'Nothing changes for employees until this draft is published.'
                : `Nothing changes for employees until ${BRAND.name} support or your Company Admin publishes it.`}
            </Alert>
          )}

          {c.status === 'SUPERSEDED' && (
            <Alert tone="info">
              Kept because it explains the screening done while it was in force.
              It is not editable and no longer applies.
            </Alert>
          )}

          <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
            <Card className="p-5">
              <h2 className="text-h4 text-neutral-900">{c.tests.length} tests</h2>
              <ul className="mt-3 divide-y divide-neutral-100">
                {c.tests.map((t) => (
                  <li key={t.id} className="flex items-center gap-3 py-2">
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="text-small text-neutral-900">{t.name}</span>
                        <StatusBadge tone={TONE[t.inclusion]}>{INCLUSION_LABEL[t.inclusion]}</StatusBadge>
                      </span>
                      <span className="mt-0.5 block font-mono text-caption text-neutral-500">{t.code}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </Card>

            <Card className="p-5">
              <h2 className="text-h4 text-neutral-900">How it was built</h2>
              <div className="mt-3 space-y-3 text-small">
                <div>
                  <div className="text-caption font-semibold uppercase tracking-wider text-neutral-500">
                    Packages
                  </div>
                  <ul className="mt-1 space-y-0.5">
                    {c.packages.map((p) => (
                      <li key={p.id} className="text-neutral-700">{p.name}</li>
                    ))}
                    {c.packages.length === 0 && <li className="text-neutral-500">None</li>}
                  </ul>
                </div>

                {/* The delta is shown, not folded away: a year from now the
                    question is "did they add this, or was it in the package?" */}
                {c.added.length > 0 && (
                  <div>
                    <div className="text-caption font-semibold uppercase tracking-wider text-neutral-500">
                      Added
                    </div>
                    <ul className="mt-1 space-y-0.5">
                      {c.added.map((t) => <li key={t.id} className="text-neutral-700">{t.name}</li>)}
                    </ul>
                  </div>
                )}

                {c.removed.length > 0 && (
                  <div>
                    <div className="text-caption font-semibold uppercase tracking-wider text-neutral-500">
                      Removed
                    </div>
                    <ul className="mt-1 space-y-0.5">
                      {c.removed.map((t) => <li key={t.id} className="text-neutral-700">{t.name}</li>)}
                    </ul>
                  </div>
                )}
              </div>

              <ObligationNote />
            </Card>
          </div>
        </>
      )}

      {confirming && c && (
        <ConfirmDialog
          title={`Publish ${c.name}?`}
          what={`It comes into force for ${c.departmentName ? `nobody — departments no longer apply` : c.jobRole ? `the ${c.jobRole.title} role` : 'every employee'}. Examinations raised from now on use its ${c.tests.length} tests.`}
          affects="Examinations already raised keep the tests they were created with."
          irreversible="A published configuration cannot be edited. Whatever was in force for the same scope is superseded and kept for history."
          confirmLabel="Publish"
          onConfirm={publish}
          onCancel={() => setConfirming(false)}
        />
      )}
    </div>
  );
}
